import ExpandableFilterPanel from 'components/molecules/expandable-filter-panel';
import BaseSearchSelect from 'components/atoms/base-search-select';
import BaseSelectBox from 'components/atoms/base-select-box';
import { type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import RpmStatusSelectBox from 'components/molecules/rpm-status-select-box';
import CcmStatusSelectBox from 'components/molecules/ccm-status-select-box';
import ClinicFilterSelectBoxV2 from 'components/molecules/include-clinic-select-box';

import s from './styles.module.scss';
import { useFilter } from './use-filter';

import type { CcmStatuses, RpmStatuses } from 'infrastructure/enums';

interface ItemProps {
  label?: string;
  children: ReactNode;
}
const Item: FC<ItemProps> = ({ label, children }) => (
  <div className={s['item-wrapper']}>
    {label && <label data-cy={`patientFilter-${label}`}>{label}</label>}
    {children}
  </div>
);

interface PatientsFilterProps {}

const PatientsFilter: FC<PatientsFilterProps> = () => {
  const { t } = useTranslation();
  const {
    filter,
    updateFilter,
    searchPatientsLoading,
    placeholder,
    debounceSearchPatients,
    patientOptions,
    countOfChangedFilterField,
    options,
    resetFilter,
  } = useFilter();

  return (
    <ExpandableFilterPanel
      filtersCount={countOfChangedFilterField}
      onReset={resetFilter}
      isExpanded
    >
      <div className={s.wrapper}>
        <Item label={t('labels.patientFilter')}>
          <div className={s['patient-item']}>
            <BaseSelectBox
              options={options}
              className={s.dropdown}
              onChange={(v) => {
                updateFilter({ patientKey: v });
              }}
              value={filter.patientKey}
            />
            <BaseSearchSelect
              isLoading={searchPatientsLoading}
              className={s.search}
              options={patientOptions}
              placeholder={placeholder}
              value={filter.patientGuid}
              onChange={(v) =>
                debounceSearchPatients({
                  searchValue: v,
                  searchKey: filter.patientKey,
                })
              }
              onSelect={(v, option) => {
                updateFilter({
                  page: undefined,
                  patientGuid: v,
                  patientLabel: option.label as string,
                });
              }}
              onClear={() => {
                updateFilter({
                  page: undefined,
                  patientGuid: undefined,
                  patientLabel: undefined,
                });
              }}
            />
          </div>
        </Item>
        <Item>
          <ClinicFilterSelectBoxV2
            useAdminApi
            showOnInteractiveTimeDashboard={false}
            included={filter?.clinicGuid}
            parentClinicGuid={filter?.parentClinicGuid}
            onChange={({ included = [], parentClinicGuid = [] }) => {
              updateFilter({
                clinicGuid: included,
                parentClinicGuid,
              });
            }}
          />
        </Item>
        <div className={s['combine-item-wrapper']}>
          <Item label={t('labels.rpmStatus')}>
            <RpmStatusSelectBox
              withAll
              placeholder={t('labels.all')}
              value={filter.status}
              onChange={(v: RpmStatuses[]) =>
                updateFilter({ status: v.length ? v : undefined })
              }
              allowClear
              mode="multiple"
              maxTagTextLength={40}
            />
          </Item>
          <Item label={t('labels.ccmStatus')}>
            <CcmStatusSelectBox
              withAll
              placeholder={t('labels.all')}
              value={filter.ccmStatus}
              onChange={(v: CcmStatuses[]) =>
                updateFilter({ ccmStatus: v.length ? v : undefined })
              }
              allowClear
              mode="multiple"
              maxTagTextLength={40}
            />
          </Item>
        </div>
      </div>
    </ExpandableFilterPanel>
  );
};
export default PatientsFilter;
